import React, { useCallback, useMemo, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { StatsCard } from 'common/components';
import { CreationTaskDrawer, TaskDetailsDrawer } from 'features/daily-plan';
import { TeamFilters } from 'features/daily-plan/components/forms/shared';
import { ENDPOINTS } from 'features/daily-plan/constants';
import useDatatable, {
  addMultipleColumns,
  buildMultiplePhonesActionsRenderer,
  buildRowActionsRendererDefinition,
  extractObjectFromDataTable,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import useMultipleSelection, { CustomTableToolbarSelect } from 'hooks/multipleSelection';
import usePageTitle from 'hooks/usePageTitle';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { When } from 'UI/components/atoms/When';
import { FPButtonMenu } from 'UI/components/molecules';
import DataTable from 'UI/components/organisms/DataTable';
import FiltersLayout from 'UI/components/templates/FiltersLayout';
import { additionalPeriodRanges } from 'UI/components/templates/SideFiltersLayout/filters';
import { backNavigateListConfig } from 'UI/constants/config';
import { PageTitles, RowActions } from 'UI/constants/defaults';
import { ENTITY_TYPE_ID_MAP } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SvgAdd } from 'UI/res';
import { createCustomStaticRanges, getFinalColumns, getTodayRange } from 'UI/utils';
import { DataTableLink } from 'UI/utils/renderers';

import { ARCHIVED_ACTIONS } from './ArchiveActions/ArchiveActions.constants';
import { usePhoneActions } from './hooks/usePhoneActions';
import {
  ArchiveActionsButton,
  ArchiveActionsDialog,
  ArchivedActionsDrawer
} from './ArchiveActions';
import {
  COLUMNS,
  COMPLETED_TASKS_STORAGE_KEY,
  EXTRA_FILTERS,
  FILTERS_DEFINITION,
  ORDER_BY_OPTIONS,
  PAGE_KEY
} from './DailyPlan.constants';
import { ActionContent, ActionLabel, MoreOptionsContainer } from './DailyPlan.styles';
import {
  formatResponse,
  getInitialTeamFilters,
  getShowCompletedTasksCheckFromStorage
} from './DailyPlan.utils';
import { RescheduleActionsButton, RescheduleActionsDialog } from './RescheduleActions';

const COPIES = strings.dailyPlan;
const ICON_BUTTON_SIZE = 16;
const COMMUNICATION_DATA_COLUMN_NAME = 'communicationData';

const ContactRoutes = {
  [ENTITY_TYPE_ID_MAP.candidate]: EntityRoutes.CandidateProfile,
  [ENTITY_TYPE_ID_MAP.company]: EntityRoutes.CompanyProfile,
  [ENTITY_TYPE_ID_MAP.hiringAuthority]: EntityRoutes.HiringAuthorityProfile,
  [ENTITY_TYPE_ID_MAP.contact]: EntityRoutes.ContactProfile
};

const TASK_FORM_MODE = {
  Open: 'Open',
  Closed: 'Closed'
};

const EXTRA_RENDERERS = {
  contactLink: value => {
    if (!value) return null;

    const { id, entityTypeId, fullName } = value;
    const route = ContactRoutes[entityTypeId];

    return id && route ? (
      <DataTableLink id={id} text={fullName} route={route} enableInTabRedirect />
    ) : null;
  }
};

export const DailyPlan = () => {
  const [currentUser] = useUserContext();

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const isBulkRescheduleEnabled = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanBulkReschedule);
  const isArchiveEnabled = checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanArchiveTasks);

  const [uiState, setUiState] = useState({
    archiveDialogMode: null,
    isArchivedActionsDrawerOpen: false,
    rescheduleDialogMode: null,
    taskFormMode: TASK_FORM_MODE.Closed,
    taskId: null
  });
  const [showCompletedTasks, setShowCompletedTasks] = useState(() =>
    getShowCompletedTasksCheckFromStorage()
  );

  const [teamFilters, setTeamFilters] = useState(() => getInitialTeamFilters(currentUser));

  const { trackCommunicationAction, getContactInfo } = usePhoneActions({
    columns: COLUMNS
  });

  const multiplePhonesActionButtons = buildMultiplePhonesActionsRenderer({
    customName: 'phoneNumbers',
    handleAction: trackCommunicationAction,
    columns: getFinalColumns(COLUMNS),
    getContactInfo
  });

  const dailyPlanColumns = addMultipleColumns(getFinalColumns(COLUMNS), [
    { column: multiplePhonesActionButtons, afterColumn: 'contactStatus' }
  ]).filter(column => !!column);

  const initialPreferences = getColumnPreferences(PAGE_KEY, 0, ORDER_BY_OPTIONS, dailyPlanColumns);

  const extendParams = useCallback(() => {
    const baseParams = {
      showCompleted: !!showCompletedTasks
    };

    if (teamFilters.recruiter?.value?.id) {
      baseParams.assignedTo = teamFilters.recruiter?.value?.id;
    } else {
      baseParams.assignedTo = currentUser.id;
    }

    return baseParams;
  }, [currentUser, showCompletedTasks, teamFilters]);

  const {
    columnPreferences,
    count,
    data,
    filters,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersApply,
    handleFiltersChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePeriodChange,
    handlePerPageChange,
    listState,
    getData
  } = useDatatable({
    columnsDefinitions: dailyPlanColumns,
    endpoint: ENDPOINTS.tasks,
    formatResponse,
    initialPreferences,
    key: PAGE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: getTodayRange(),
    periodFilterColumn: 'date',
    shouldScrollOnNavigate: true,
    sendDateWithTimezone: true,
    paramsExtender: extendParams
  });

  const { isLoading, showWholeSkeleton, isSideMenuOpen, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const {
    filteredItems,
    handleRowSelection,
    multiSelectComponents,
    selectedIndexesInPage,
    setToInitial,
    totalSelected
  } = useMultipleSelection({ data, count, perPage, disableSelectAllCheckbox: true });

  const selectedItemsIds = useMemo(
    () => filteredItems.data.map(item => item.id),
    [filteredItems.data]
  );

  const refreshTable = async () => {
    handleOpenRescheduleDialog(null);
    handleOpenArchiveTasksDialog(null);
    setToInitial();
    await getData();
  };

  const handleTeamFiltersChange = useCallback(
    newTeamFilters => {
      setTeamFilters(newTeamFilters);
      handlePageChange(0);
    },
    [handlePageChange]
  );

  const handleViewTaskDetails = async ({ rowData }) => {
    if (!rowData) return;

    const item = extractObjectFromDataTable(dailyPlanColumns, ['id'], rowData);

    setUiState(prev => ({
      ...prev,
      taskId: item.id
    }));
  };

  const actionsButton = buildRowActionsRendererDefinition({
    actions: [
      {
        name: RowActions.Call,
        value: 'phone'
      },
      {
        name: RowActions.Email,
        value: 'email'
      },
      {
        name: RowActions.Text,
        value: 'phone'
      },
      { name: RowActions.View, onClick: handleViewTaskDetails }
    ],
    columns: dailyPlanColumns,
    handleAction: trackCommunicationAction,
    customName: COMMUNICATION_DATA_COLUMN_NAME
  });
  const finalColumns = getColumnsToRender([...columns, actionsButton], EXTRA_RENDERERS);

  usePageTitle({ title: PageTitles.DailyPlan, isLoading });

  const handleCloseTaskDetailsDrawer = () => {
    setUiState(prev => ({
      ...prev,
      taskId: null
    }));
  };

  const handleCloseActionPlanDrawer = () => {
    setUiState(prev => ({
      ...prev,
      taskFormMode: TASK_FORM_MODE.Closed
    }));
  };

  const handleOpenDrawer = () => {
    setUiState(prev => ({
      ...prev,
      taskFormMode: TASK_FORM_MODE.Open
    }));
  };

  const handleCheckToggle = (event, checked) => {
    setShowCompletedTasks(checked);
    localStorage.setItem(COMPLETED_TASKS_STORAGE_KEY, JSON.stringify(checked));
  };

  const handleOnTaskCreated = syncFilters => {
    syncFilters ? syncFilters() : getData();
    handleCloseActionPlanDrawer();
  };

  const handleOpenRescheduleDialog = mode => {
    setUiState(prev => ({ ...prev, rescheduleDialogMode: mode }));
  };

  const handleOpenArchiveTasksDialog = mode => {
    setUiState(prev => ({ ...prev, archiveDialogMode: mode }));
  };

  const moreOptions = [
    {
      title: strings.dailyPlan.archiveActions.drawer.title,
      visible: true,
      action: () => setUiState(prev => ({ ...prev, isArchivedActionsDrawerOpen: true }))
    }
  ];

  const headerAction = (
    <FormControlLabel
      control={
        <Checkbox
          color="primary"
          checked={showCompletedTasks}
          onChange={handleCheckToggle}
          name="showCompletedTasks"
        />
      }
      label={
        <ActionLabel color="textSecondary" variant="body1">
          {COPIES.header.action}
        </ActionLabel>
      }
      onClick={e => e.stopPropagation()}
    />
  );

  return (
    <FiltersLayout
      dateRangerPickerProps={{
        staticRanges: createCustomStaticRanges(additionalPeriodRanges)
      }}
      defaultRange={getTodayRange()}
      enableStore={false}
      extraFiltersDefinition={EXTRA_FILTERS}
      filters={filters}
      includeFilters={FILTERS_DEFINITION}
      isLoading={showWholeSkeleton}
      isSideMenuOpen={isSideMenuOpen}
      onFiltersChange={handleFiltersChange}
      onMenuToggle={handleFiltersToggle}
      extraSelector={
        <When condition={isArchiveEnabled}>
          <MoreOptionsContainer>
            <FPButtonMenu
              title={strings.dailyPlan.moreOptions.title}
              isIconButton
              variant="outlinePrimary"
              menuItems={moreOptions}
            />
          </MoreOptionsContainer>
        </When>
      }
      onPeriodChange={handlePeriodChange}
      onSearch={handleFiltersApply}
      subtitle={<Typography color="textSecondary">{COPIES.subtitle}</Typography>}
      title={COPIES.title}
      titleLabelProps={backNavigateListConfig}
    >
      <Grid container spacing={4}>
        <TeamFilters onFilterChange={handleTeamFiltersChange} initialFilters={teamFilters} />

        <ActionContent item xs={12}>
          <FPActionButton
            icon={SvgAdd}
            text={COPIES.actionButton}
            onClick={handleOpenDrawer}
            iconProps={{ family: 'mini', size: ICON_BUTTON_SIZE }}
          />
        </ActionContent>

        <Grid item xs={12}>
          <StatsCard
            header={COPIES.header.title}
            headerAction={headerAction}
            paddingContent={0}
            footer={null}
          >
            <DataTable
              columnOrder={columnOrder?.length && columnOrder}
              columns={finalColumns}
              components={multiSelectComponents}
              count={count ?? 0}
              CustomToolbar={
                <CustomTableToolbarSelect total={totalSelected}>
                  <When condition={isBulkRescheduleEnabled}>
                    <RescheduleActionsButton
                      selectedItems={filteredItems}
                      onOpenRescheduleDialog={handleOpenRescheduleDialog}
                    />
                  </When>
                  <When condition={isArchiveEnabled}>
                    <ArchiveActionsButton
                      mode={ARCHIVED_ACTIONS.Archive}
                      onClick={() => handleOpenArchiveTasksDialog(ARCHIVED_ACTIONS.Archive)}
                    />
                  </When>
                </CustomTableToolbarSelect>
              }
              data={data}
              draggableColumns={{
                enabled: true
              }}
              elevation={0}
              filter={false}
              isExpandable
              loading={showWholeSkeleton}
              onColumnDisplayClick={handleColumnDisplayChange}
              onColumnOrderChange={handleColumnOrderChange}
              onColumnSortChange={handleColumnSortChange}
              onPageClick={handlePageChange}
              onPerPageClick={handlePerPageChange}
              onRowSelectionChange={handleRowSelection}
              onSearchTextChange={handleKeywordChange}
              onToggleFilters={handleFiltersToggle}
              page={page}
              refreshing={isLoading}
              rowsPerPage={perPage}
              rowsSelected={selectedIndexesInPage}
              searchText={keyword}
              selectableRows={isBulkRescheduleEnabled ? 'multiple' : 'none'}
              selectToolbarPlacement="none"
              sortOrder={{ name: orderBy, direction }}
              subtitle={COPIES.emptyState.tasks.subtitle}
              title={COPIES.emptyState.tasks.title}
            />
          </StatsCard>
        </Grid>
      </Grid>

      <When condition={uiState.taskFormMode === TASK_FORM_MODE.Open}>
        <CreationTaskDrawer
          onClose={handleCloseActionPlanDrawer}
          onTeamFiltersChange={handleTeamFiltersChange}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>

      <When condition={!!uiState.taskId}>
        <TaskDetailsDrawer
          onClose={handleCloseTaskDetailsDrawer}
          taskId={uiState.taskId}
          onTaskUpdated={getData}
        />
      </When>

      <When condition={uiState.isArchivedActionsDrawerOpen}>
        <ArchivedActionsDrawer
          extendParams={extendParams}
          onClose={() => setUiState(prev => ({ ...prev, isArchivedActionsDrawerOpen: false }))}
          onRestoreComplete={refreshTable}
        />
      </When>

      <When condition={!!uiState.rescheduleDialogMode}>
        <RescheduleActionsDialog
          mode={uiState.rescheduleDialogMode}
          onClose={() => handleOpenRescheduleDialog(null)}
          onRescheduleComplete={refreshTable}
          selectedItems={filteredItems}
        />
      </When>

      <When condition={!!uiState.archiveDialogMode}>
        <ArchiveActionsDialog
          mode={ARCHIVED_ACTIONS.Archive}
          onClose={() => handleOpenArchiveTasksDialog(null)}
          onComplete={refreshTable}
          selectedItems={selectedItemsIds}
        />
      </When>
    </FiltersLayout>
  );
};
